import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import { openCsvImportModal } from "../components/csv-import-modal";
import type { Practice, Department, DepartmentGroup, PlanningPeriod } from "../types";
import { exportPracticeAssignments, type PracticeExportData } from "../utils/export-service";
import { canWrite, writeOnly, readOnlyNotice, lockInputs } from "../utils/permissions";
import { renderDocumentsTab, attachDocumentsListeners } from "./student-documents";
import { esc } from "../utils/html";

let practices: Practice[] = [];
let departments: Department[] = [];
let allGroups: DepartmentGroup[] = [];
let kapazitaetProZeitraumEnabled = false;
let currentEl: HTMLElement | null = null;
let searchQuery = "";

let ellipsisTooltip: HTMLDivElement | null = null;
function showEllipsisTooltip(td: HTMLElement) {
  if (td.scrollWidth <= td.clientWidth) return;
  if (!ellipsisTooltip) {
    ellipsisTooltip = document.createElement("div");
    ellipsisTooltip.style.cssText = "position:fixed;z-index:9999;background:#fff;border:1px solid var(--gray-200);box-shadow:0 2px 12px rgba(0,0,0,.1);padding:1rem 1.5rem;font-size:.875rem;white-space:nowrap;pointer-events:none;display:none;";
    document.body.appendChild(ellipsisTooltip);
  }
  ellipsisTooltip.textContent = td.getAttribute("data-full") || "";
  ellipsisTooltip.style.fontWeight = td.style.fontWeight || "";
  ellipsisTooltip.style.color = td.classList.contains("text-muted") ? "var(--gray-500)" : "";
  const r = td.getBoundingClientRect();
  ellipsisTooltip.style.left = r.left + "px";
  ellipsisTooltip.style.top = r.top + "px";
  ellipsisTooltip.style.minHeight = r.height + "px";
  ellipsisTooltip.style.display = "block";
}
function hideEllipsisTooltip() {
  if (ellipsisTooltip) ellipsisTooltip.style.display = "none";
}

async function load() {
  const [p, d, s, g] = await Promise.all([
    api.practices.list(undefined, true), api.departments(), api.settings.get(), api.settings.groupsWithOverrides()
  ]);
  practices = p;
  departments = d;
  kapazitaetProZeitraumEnabled = s.kapazitaetProZeitraumEnabled ?? false;
  allGroups = g;
}

// Global handlers for onclick attributes (use == for string/number comparison)
(window as any).__pEdit = (id: number) => {
  try {
    const p = practices.find(pr => pr.id == id);
    if (p) openForm(p);
  } catch (err: any) { alert("Fehler: " + err.message); }
};
(window as any).__pDel = async (id: number) => {
  try {
    if (!confirm("Löschen?")) return;
    await api.practices.delete(id);
    await load();
    if (currentEl) renderTable(currentEl);
  } catch (err: any) { alert("Fehler: " + err.message); }
};
(window as any).__pToggle = async (id: number) => {
  try {
    await api.practices.toggle(id);
    await load();
    if (currentEl) renderTable(currentEl);
  } catch (err: any) { alert("Fehler: " + err.message); }
};

function renderTable(el: HTMLElement) {
  const tbody = el.querySelector("tbody")!;
  const q = searchQuery.trim().toLowerCase();
  const catLabel = (cat: string) => cat === "KRANKENHAUS" ? "krankenhaus" : "praxis";
  const filtered = q
    ? practices.filter(p => [p.name, p.plz, p.ort, catLabel(p.category ?? "")].some(v => (v || "").toLowerCase().includes(q)))
    : practices;
  if (filtered.length === 0) { tbody.innerHTML = `<tr><td colspan="${canWrite() ? 7 : 6}" class="empty-state">${q ? "Keine Treffer" : "Keine Praktikumsstellen vorhanden"}</td></tr>`; return; }
  tbody.innerHTML = filtered.map(p => {
    const catBadge = p.category === "KRANKENHAUS" ? `<span class="badge badge-blue">Krankenhaus</span>` : `<span class="badge badge-green">Praxis</span>`;
    const geoIcon = p.latitude && p.longitude
      ? `<span class="badge badge-green" title="Koordinaten vorhanden">&#10003;</span>`
      : `<span class="badge" style="background:var(--red-50);color:var(--red-700)" title="Keine Koordinaten">&#10007;</span>`;
    // Betrachter können den Status nur ablesen, nicht umschalten
    const activeBadge = canWrite()
      ? (p.active
        ? `<span class="badge badge-blue" title="Aktiv – klicken zum Deaktivieren" style="cursor:pointer" onclick="event.stopPropagation();window.__pToggle(${p.id})">Aktiv</span>`
        : `<span class="badge" style="background:var(--gray-100);color:var(--gray-500);cursor:pointer" title="Inaktiv – klicken zum Aktivieren" onclick="event.stopPropagation();window.__pToggle(${p.id})">Inaktiv</span>`)
      : (p.active
        ? `<span class="badge badge-blue">Aktiv</span>`
        : `<span class="badge" style="background:var(--gray-100);color:var(--gray-500)">Inaktiv</span>`);
    const rowStyle = p.active ? "cursor:pointer" : "cursor:pointer;opacity:0.5";
    return `<tr style="${rowStyle}" onclick="window.__pEdit(${p.id})">
      ${writeOnly(`<td onclick="event.stopPropagation()"><input type="checkbox" class="row-sel" data-id="${p.id}" onchange="window.__pUpdateSel()" /></td>`)}
      <td data-full="${esc(p.name)}" style="font-weight:500;max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(p.name)}</td><td class="text-muted">${p.plz || ""}</td>
      <td data-full="${esc(p.ort || "")}" class="text-muted" style="max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${p.ort || ""}</td><td>${catBadge}</td>
      <td class="text-center">${activeBadge}</td>
      <td class="text-center">${geoIcon}</td></tr>`;
  }).join("");

}

function openForm(p?: Practice) {
  const isEdit = !!p;

  // Kapazitäten-Tab: nach Fachbereichsgruppe gruppiert, 2-spaltig
  const deptCard = (d: Department) => `
    <div style="border-left:3px solid ${d.color};padding:.5rem .75rem;background:var(--gray-50);border-radius:0 .375rem .375rem 0">
      <div style="font-weight:600;color:${d.color};font-size:.875rem;margin-bottom:.375rem">${esc(d.nameDe)} <span style="font-weight:400;color:var(--gray-500)">(${esc(d.shortName)})</span></div>
      <div class="form-group" style="margin:0">
        <label style="font-size:.8125rem">Gesamtplätze</label>
        <input type="number" min="0" id="f-cap-${d.id}" value="${p?.capacities?.[d.id] || 0}" />
      </div>
      ${kapazitaetProZeitraumEnabled ? `<div class="form-group" style="margin-top:.25rem;margin-bottom:0">
        <label style="font-size:.8125rem">Max. pro Zeitraum <span style="color:var(--gray-400)">(leer = unbegrenzt)</span></label>
        <input type="number" min="1" id="f-ppc-${d.id}" placeholder="unbegrenzt" value="${p?.perPeriodCapacities?.[d.id] != null ? p.perPeriodCapacities[d.id] : ""}" />
      </div>` : ""}
    </div>`;

  const shownGroups = allGroups.filter(g => g.isActive !== false);
  const grouped = shownGroups
    .map(g => ({ g, depts: departments.filter(d => g.departments.some(x => x.id === d.id)) }))
    .filter(x => x.depts.length > 0);
  const ungrouped = departments.filter(d => !shownGroups.some(g => g.departments.some(x => x.id === d.id)));

  const groupSection = (label: string, depts: Department[]) => `
    <div style="margin-bottom:1.25rem">
      <div style="font-size:.8125rem;font-weight:600;text-transform:uppercase;letter-spacing:.06em;color:var(--gray-500);margin-bottom:.5rem;padding-bottom:.25rem;border-bottom:1px solid var(--gray-200)">${esc(label)}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:.5rem">${depts.map(deptCard).join("")}</div>
    </div>`;

  const capSections = departments.length === 0
    ? `<p style="color:var(--gray-400);font-size:.875rem">Noch keine Fachbereiche vorhanden. Ordne unter „Klassen“ einer Klasse eine Fachbereichsgruppe zu, dann erscheinen deren Fachbereiche hier.</p>`
    : [
        ...grouped.map(({ g, depts }) => groupSection(g.name, depts)),
        ungrouped.length > 0 ? groupSection("Eigene Fachbereiche", ungrouped) : "",
      ].join("");

  const linked = !!p?.practiceOrganizationId;
  const lockAttr = linked ? "disabled" : "";
  const lockClass = linked ? "input-locked" : "";
  const lockHint = linked ? `<small style="color:var(--gray-500)">Aus Praktikumsstellen-Profil übernommen</small>` : "";

  openModal(isEdit ? "Praktikumsstelle bearbeiten" : "Neue Praktikumsstelle", `
    <div class="detail-tabs">
      <button type="button" class="detail-tab active" data-tab="stammdaten">Stammdaten</button>
      <button type="button" class="detail-tab" data-tab="kapazitaeten">Kapazitäten</button>
      <button type="button" class="detail-tab" data-tab="planung">Planung</button>
      ${isEdit && linked ? `<button type="button" class="detail-tab" data-tab="dokumente">Dokumente</button>` : ""}
    </div>
    <div class="tab-content-wrap">
      <div data-tab-content="stammdaten" style="padding-top:.25rem">
        <div class="connect-section">
          <div id="connect-disconnected" class="${linked ? 'hidden' : ''}">
            <label style="font-weight:600;font-size:.8125rem">Praktikumsstellen-Code <span style="color:var(--gray-500);font-weight:normal">(optional)</span></label>
            <div style="display:flex;gap:.5rem;margin-top:.375rem">
              <input id="f-connect-code" placeholder="MV-XXXXXX" style="text-transform:uppercase;flex:1" />
              ${writeOnly(`<button type="button" class="btn btn-primary" id="btn-connect">Verbinden</button>`)}
            </div>
            <small style="color:var(--gray-500);display:block;margin-top:.375rem">Wenn die Praktikumsstelle bei Meduvio registriert ist, kannst du sie hier verknüpfen — die Stammdaten werden dann automatisch übernommen und gepflegt.</small>
            <small id="connect-error" style="color:var(--red-700);display:none;margin-top:.375rem"></small>
          </div>
          <div id="connect-linked" class="${linked ? '' : 'hidden'}">
            <div class="linked-badge">
              <span>&#10003;</span>
              Verknüpft mit <strong>${esc(p?.name || "")}</strong> · Code <code>${esc(p?.linkedCode || "")}</code>
            </div>
            ${writeOnly(`<button type="button" class="btn btn-secondary btn-sm" id="btn-disconnect">Verbindung aufheben</button>`)}
          </div>
        </div>
        <fieldset><legend>Grunddaten</legend><div class="form-grid">
          <div class="form-group col-span-2"><label>Name *</label><input id="f-name" value="${esc(p?.name || "")}" ${lockAttr} class="${lockClass}" />${lockHint}</div>
          <div class="form-group"><label>Straße</label><input id="f-street" value="${esc(p?.street || "")}" ${lockAttr} class="${lockClass}" /></div>
          <div class="form-group"><label>Hausnr.</label><input id="f-hno" value="${esc(p?.houseNo || "")}" ${lockAttr} class="${lockClass}" /></div>
          <div class="form-group"><label>PLZ</label><input id="f-plz" value="${esc(p?.plz || "")}" ${lockAttr} class="${lockClass}" /></div>
          <div class="form-group"><label>Ort</label><input id="f-ort" value="${esc(p?.ort || "")}" ${lockAttr} class="${lockClass}" /></div>
          <div class="form-group"><label>Kategorie</label><select id="f-cat" ${lockAttr} class="${lockClass}"><option value="PRAXIS" ${p?.category !== "KRANKENHAUS" ? "selected" : ""}>Praxis</option><option value="KRANKENHAUS" ${p?.category === "KRANKENHAUS" ? "selected" : ""}>Krankenhaus</option></select></div>
          <div class="form-group"><label>Koordinaten</label><div style="display:flex;align-items:center;gap:.5rem"><div id="f-geo-status" style="font-size:.8125rem">${p?.latitude && p?.longitude ? `<span style="color:var(--green-700)">&#10003; (${p.latitude.toFixed(4)}, ${p.longitude.toFixed(4)})</span>` : `<span style="color:var(--red-700)">&#10007; Nicht ermittelt</span>`}</div>${writeOnly(`<button type="button" class="btn btn-sm btn-secondary" id="f-geocode" style="white-space:nowrap">Ermitteln</button>`)}</div></div>
          <div class="form-group"><label>Status</label><label style="display:flex;align-items:center;gap:.375rem;cursor:pointer;font-weight:normal"><input type="checkbox" id="f-active" ${p?.active !== false ? "checked" : ""} style="width:16px;height:16px;accent-color:var(--primary)" />Aktiv</label></div>
        </div></fieldset>
        <fieldset><legend>Anleitung &amp; Kontakt</legend><div class="form-grid">
          <div class="form-group"><label>Therapeutische Leitung</label><input id="f-lp" value="${esc(p?.leadingContact || "")}" /></div>
          <div class="form-group"><label>Tel. Therapeutische Leitung</label><input id="f-lpp" value="${esc(p?.leadingContactPhone || "")}" /></div>
          <div class="form-group"><label>E-Mail Therapeutische Leitung</label><input id="f-lpe" value="${esc(p?.leadingContactEmail || "")}" /></div>
          <div class="form-group"><label>Anleiter</label><input id="f-inst" value="${esc(p?.instructor || "")}" /></div>
          <div class="form-group"><label>Tel. Anleiter</label><input id="f-instp" value="${esc(p?.instructorPhone || "")}" /></div>
          <div class="form-group"><label>E-Mail Anleiter</label><input id="f-inste" value="${esc(p?.instructorEmail || "")}" /></div>
        </div></fieldset>
        <fieldset><legend>Informationen für Schüler <span class="label-hint">(für Schüler sichtbar)</span></legend><div class="form-grid">
          <div class="form-group"><label>Kontaktperson</label><input id="f-cp" value="${esc(p?.contactPersonStudents || "")}" /></div>
          <div class="form-group"><label>Telefon</label><input id="f-ps" value="${esc(p?.phoneStudents || "")}" /></div>
          <div class="form-group"><label>E-Mail</label><input id="f-es" value="${esc(p?.emailStudents || "")}" /></div>
          <div class="form-group"><label>Fahrzeit (Min.)</label><input id="f-tt" value="${esc(p?.travelTimeMinutes || "")}" /></div>
          <div class="form-group col-span-2"><label>Kommentar</label><textarea id="f-cs">${esc(p?.commentStudents || "")}</textarea></div>
        </div></fieldset>
        <fieldset><legend>Interne Notizen <span class="label-hint">(nur Schule)</span></legend><div class="form-grid">
          <div class="form-group col-span-2"><label>Internes Kommentar</label><textarea id="f-ci">${esc(p?.commentInternal || "")}</textarea></div>
        </div></fieldset>
        <fieldset><legend>Sonstiges</legend><div class="form-grid">
          <div class="form-group"><label>Sichtstunden</label><input id="f-vh" value="${esc(p?.visitingHours || "")}" /></div>
          <div class="form-group"><label>Bewerbungsunterlagen</label><input id="f-ad" value="${esc(p?.applicationDocuments || "")}" /></div>
          <div class="form-group col-span-2"><label>Wichtige Änderungen</label><textarea id="f-ic">${esc(p?.importantChanges || "")}</textarea></div>
        </div></fieldset>
      </div>
      <div data-tab-content="kapazitaeten" style="display:none;padding-top:.25rem">
        ${capSections}
      </div>
      <div data-tab-content="planung" style="display:none;padding-top:.5rem">
        ${isEdit ? `<p style="color:var(--gray-400);font-size:.875rem">Klicke auf den Tab, um die Planung zu laden.</p>` : `<p style="color:var(--gray-400);font-size:.875rem">Bitte zuerst speichern, um die Planung anzuzeigen.</p>`}
      </div>
      ${isEdit && linked ? `<div data-tab-content="dokumente" style="display:none;padding-top:.5rem">${renderDocumentsTab()}</div>` : ""}
    </div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="f-cancel">${canWrite() ? "Abbrechen" : "Schließen"}</button>
      ${writeOnly(`<span id="f-save-tip" style="display:inline-block">
        <button class="btn btn-primary" id="f-save">Speichern</button>
      </span>`)}
    </div>`, { wide: true, fixedSize: true });

  const mb = modalBody();
  // Betrachter sehen die Maske schreibgeschützt
  lockInputs(mb);
  let geoLat: number | null = p?.latitude || null;
  let geoLng: number | null = p?.longitude || null;

  // Tab-Wechsel
  let planLoaded = false;
  let docsLoaded = false;
  mb.querySelectorAll<HTMLButtonElement>(".detail-tab").forEach(btn => {
    btn.addEventListener("click", () => {
      mb.querySelectorAll<HTMLButtonElement>(".detail-tab").forEach(b => b.classList.remove("active"));
      mb.querySelectorAll<HTMLElement>("[data-tab-content]").forEach(c => c.style.display = "none");
      btn.classList.add("active");
      (mb.querySelector(`[data-tab-content="${btn.dataset.tab!}"]`) as HTMLElement).style.display = "";
      if (btn.dataset.tab === "planung" && isEdit && !planLoaded) {
        planLoaded = true;
        renderPlanungsTab(p!.id, p!, mb);
      }
      if (btn.dataset.tab === "dokumente" && isEdit && linked && !docsLoaded) {
        docsLoaded = true;
        const docsTab = mb.querySelector<HTMLElement>('[data-tab-content="dokumente"]')!;
        attachDocumentsListeners({
          list: () => api.practiceDocuments.list(p!.id),
          upload: (file) => api.practiceDocuments.upload(p!.id, file),
          download: (docId, name) => api.practiceDocuments.download(p!.id, docId, name),
          delete: (docId) => api.practiceDocuments.delete(p!.id, docId),
          rename: (docId, name) => api.practiceDocuments.rename(p!.id, docId, name),
          merge: (docIds, filename) => api.practiceDocuments.merge(p!.id, docIds, filename),
        }, docsTab);
      }
    });
  });

  // Validierung: Speichern nur aktiv wenn Pflichtfelder gefüllt
  // (entfällt für Betrachter, die haben keine Speichern-Schaltfläche)
  const saveBtn = mb.querySelector("#f-save") as HTMLButtonElement | null;
  const saveTip = mb.querySelector("#f-save-tip") as HTMLElement | null;
  const validateForm = () => {
    if (!saveBtn || !saveTip) return;
    const name = (mb.querySelector("#f-name") as HTMLInputElement)?.value.trim();
    if (!name) {
      saveBtn.disabled = true;
      saveTip.title = "Pflichtfelder ausfüllen: Name";
    } else {
      saveBtn.disabled = false;
      saveTip.title = "";
    }
  };
  (mb.querySelector("#f-name") as HTMLInputElement).addEventListener("input", validateForm);
  validateForm();

  mb.querySelector("#f-geocode")?.addEventListener("click", async () => {
    const btn = mb.querySelector("#f-geocode") as HTMLButtonElement;
    const geoStatus = mb.querySelector("#f-geo-status")!;
    btn.disabled = true;
    btn.textContent = "Suche…";
    geoStatus.innerHTML = `<span style="color:var(--gray-500)">Adresse wird gesucht…</span>`;
    const geo = await geocodeAddress(val("#f-street"), val("#f-hno"), val("#f-plz"), val("#f-ort"));
    if (geo) {
      geoLat = geo.lat; geoLng = geo.lng;
      geoStatus.innerHTML = `<span style="color:var(--green-700)">&#10003; (${geo.lat.toFixed(4)}, ${geo.lng.toFixed(4)})</span>`;
    } else {
      geoLat = null; geoLng = null;
      geoStatus.innerHTML = `<span style="color:var(--red-700)">&#10007; Adresse nicht gefunden</span>`;
    }
    btn.disabled = false;
    btn.textContent = "Ermitteln";
  });

  // ── Connect-Handler (Code → Praktikumsstellen-Organisation verknüpfen) ──
  let pendingCode: string | null = null; // beim Neuanlegen: Code wird beim Submit mitgesendet
  let isLinked = linked;

  const connectErr = mb.querySelector("#connect-error") as HTMLElement;
  const btnConnect = mb.querySelector("#btn-connect") as HTMLButtonElement | null;
  btnConnect?.addEventListener("click", async () => {
    // Codes mit dem alten Präfix MS- (bis 2.0.0) gelten weiter, siehe normalizePracticeCode im Backend
    const code = ((mb.querySelector("#f-connect-code") as HTMLInputElement).value || "").trim().toUpperCase().replace(/^MS-/, "MV-");
    connectErr.style.display = "none";
    if (!/^MV-[A-Z0-9]{6}$/.test(code)) {
      connectErr.textContent = "Code-Format: MV-XXXXXX (6 Zeichen, Großbuchstaben/Zahlen).";
      connectErr.style.display = "block";
      return;
    }
    btnConnect.disabled = true; btnConnect.textContent = "Prüfe…";
    try {
      const lookup = await api.practices.lookupByCode(code);
      if (lookup.alreadyLinkedTo) {
        // Modal-Warning, kein Connect.
        openModal("Code bereits verknüpft", `
          <p>Dieser Code ist in deiner Schule bereits mit der Praktikumsstelle <strong>${esc(lookup.alreadyLinkedTo.name)}</strong> verbunden.</p>
          <p>Ein Code kann pro Schule nur einer Praktikumsstelle zugeordnet sein. Hebe die bestehende Verbindung auf, bevor du sie hier neu verknüpfst.</p>
          <div class="form-actions" style="justify-content:flex-end">
            <button class="btn btn-primary" id="dup-close">Schließen</button>
          </div>`, { wide: false });
        modalBody().querySelector("#dup-close")!.addEventListener("click", closeModal);
        return;
      }
      if (isEdit && p) {
        // Direkter Connect für bestehende Practice.
        await api.practices.connect(p.id, code);
        closeModal();
        await load();
        if (currentEl) renderTable(currentEl);
        // Neu öffnen mit aktualisiertem Stand, damit der User die übernommenen Daten sieht.
        const fresh = practices.find(x => x.id === p.id);
        if (fresh) openForm(fresh);
      } else {
        // Beim Neuanlegen: Code merken + Felder im UI bereits vorab füllen (read-only),
        // beim Submit wird der Code an `POST /` mitgesendet → atomarer Connect.
        pendingCode = code;
        isLinked = true;
        (mb.querySelector("#f-name") as HTMLInputElement).value = lookup.org.name;
        (mb.querySelector("#f-street") as HTMLInputElement).value = lookup.org.street || "";
        (mb.querySelector("#f-hno") as HTMLInputElement).value = lookup.org.house_no || "";
        (mb.querySelector("#f-plz") as HTMLInputElement).value = lookup.org.plz || "";
        (mb.querySelector("#f-ort") as HTMLInputElement).value = lookup.org.ort || "";
        (mb.querySelector("#f-cat") as HTMLSelectElement).value = lookup.org.category || "PRAXIS";
        ["#f-name", "#f-street", "#f-hno", "#f-plz", "#f-ort", "#f-cat"].forEach(sel => {
          const el = mb.querySelector(sel) as HTMLInputElement | HTMLSelectElement;
          el.setAttribute("disabled", "");
          el.classList.add("input-locked");
        });
        (mb.querySelector("#connect-disconnected") as HTMLElement).classList.add("hidden");
        const linkedEl = mb.querySelector("#connect-linked") as HTMLElement;
        linkedEl.classList.remove("hidden");
        linkedEl.querySelector(".linked-badge")!.innerHTML =
          `<span>&#10003;</span> Verknüpft mit <strong>${esc(lookup.org.name)}</strong> · Code <code>${esc(lookup.org.code)}</code>`;
        validateForm();
      }
    } catch (err: any) {
      connectErr.textContent = err.message || "Code nicht gefunden.";
      connectErr.style.display = "block";
    } finally {
      btnConnect.disabled = false; btnConnect.textContent = "Verbinden";
    }
  });

  const btnDisconnect = mb.querySelector("#btn-disconnect") as HTMLButtonElement | null;
  btnDisconnect?.addEventListener("click", () => {
    openModal("Verbindung aufheben", `
      <p>Soll die Verknüpfung mit der Praktikumsstelle <strong>${esc(p?.name || "")}</strong> wirklich aufgehoben werden?</p>
      <p style="color:var(--gray-600);font-size:.875rem">Die aktuellen Stammdaten bleiben erhalten und werden danach wieder editierbar. Eine erneute Verbindung ist jederzeit möglich.</p>
      <div style="display:flex;gap:.6rem;align-items:flex-start;padding:.75rem .9rem;background:var(--red-50);border:1px solid var(--red-200);border-radius:.5rem;margin-bottom:1rem">
        <span style="color:var(--red-700);font-size:1.05rem;line-height:1.2;flex-shrink:0">&#9888;</span>
        <span style="color:var(--red-700);font-size:.875rem">Achtung: Alle mit dieser Praktikumsstelle ausgetauschten Dokumente werden dabei <strong>unwiderruflich gelöscht</strong> und können nicht wiederhergestellt werden.</span>
      </div>
      <div class="form-actions" style="justify-content:flex-end;gap:.5rem">
        <button class="btn btn-secondary" id="dc-cancel">Abbrechen</button>
        <button class="btn btn-danger" id="dc-confirm">Verbindung aufheben &amp; Dokumente löschen</button>
      </div>`, { wide: false });
    const mb2 = modalBody();
    mb2.querySelector("#dc-cancel")!.addEventListener("click", () => { closeModal(); openForm(p); });
    mb2.querySelector("#dc-confirm")!.addEventListener("click", async () => {
      if (!p) return;
      const confirmBtn = mb2.querySelector("#dc-confirm") as HTMLButtonElement;
      confirmBtn.disabled = true; confirmBtn.textContent = "Trenne…";
      try {
        await api.practices.disconnect(p.id);
        closeModal();
        await load();
        if (currentEl) renderTable(currentEl);
        const fresh = practices.find(x => x.id === p.id);
        if (fresh) openForm(fresh);
      } catch (err: any) {
        confirmBtn.disabled = false; confirmBtn.textContent = "Verbindung aufheben & Dokumente löschen";
        alert(err.message || "Trennen fehlgeschlagen");
      }
    });
  });

  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#f-save")?.addEventListener("click", async () => {
    const caps: Record<string, number> = {};
    departments.forEach(d => { caps[d.id] = parseInt((mb.querySelector(`#f-cap-${d.id}`) as HTMLInputElement).value) || 0; });
    const perPeriodCaps: Record<string, number | null> = {};
    if (kapazitaetProZeitraumEnabled) {
      departments.forEach(d => {
        const raw = (mb.querySelector(`#f-ppc-${d.id}`) as HTMLInputElement)?.value;
        perPeriodCaps[d.id] = raw && parseInt(raw) > 0 ? parseInt(raw) : null;
      });
    }

    // Auto-geocode if address changed or no coordinates yet (auch für verknüpfte
    // Practices – der Server persistiert die hier ermittelten lat/lng dann nur noch).
    const curStreet = val("#f-street"), curHno = val("#f-hno"), curPlz = val("#f-plz"), curOrt = val("#f-ort");
    const addrChanged = curStreet !== (p?.street || "") || curHno !== (p?.houseNo || "") || curPlz !== (p?.plz || "") || curOrt !== (p?.ort || "");
    if ((!geoLat && (curStreet || curPlz || curOrt)) || addrChanged) {
      if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = "Koordinaten werden ermittelt…"; }
      const geo = await geocodeAddress(curStreet, curHno, curPlz, curOrt);
      if (geo) { geoLat = geo.lat; geoLng = geo.lng; }
      else if (addrChanged) { geoLat = null; geoLng = null; }
      if (saveBtn) saveBtn.textContent = "Speichern";
      validateForm();
    }

    const data: any = {
      name: val("#f-name"), street: curStreet, houseNo: curHno, plz: curPlz, ort: curOrt,
      category: val("#f-cat"), leadingContact: val("#f-lp"), leadingContactPhone: val("#f-lpp"), leadingContactEmail: val("#f-lpe"),
      instructor: val("#f-inst"), instructorPhone: val("#f-instp"), instructorEmail: val("#f-inste"),
      contactPersonStudents: val("#f-cp"), phoneStudents: val("#f-ps"), emailStudents: val("#f-es"),
      travelTimeMinutes: val("#f-tt"), commentStudents: val("#f-cs"), commentInternal: val("#f-ci"),
      visitingHours: val("#f-vh"), applicationDocuments: val("#f-ad"), importantChanges: val("#f-ic"),
      capacities: caps,
      perPeriodCapacities: perPeriodCaps,
      latitude: geoLat,
      longitude: geoLng,
      active: (mb.querySelector("#f-active") as HTMLInputElement).checked,
    };
    if (!data.name) return;
    if (!isEdit && pendingCode) data.code = pendingCode;
    if (isEdit) await api.practices.update(p!.id, data); else await api.practices.create(data);
    closeModal(); await load(); if (currentEl) renderTable(currentEl);
  });
  function val(sel: string) { return (mb.querySelector(sel) as HTMLInputElement)?.value || ""; }
}

export async function render(el: HTMLElement) {
  currentEl = el;
  searchQuery = "";
  el.innerHTML = `<div class="page-header"><h1>Praktikumsstellen</h1><div style="display:flex;gap:.5rem">${writeOnly(`<button class="btn btn-secondary" id="btn-csv">CSV Import</button><button class="btn btn-primary" id="btn-add">+ Neue Praktikumsstelle</button>`)}</div></div>
    ${readOnlyNotice()}
    <div class="filter-bar"><input type="search" id="search-query" data-readable="true" placeholder="Suchen …" />${writeOnly(`<button class="btn btn-danger btn-sm" id="btn-del-sel-p" disabled>Auswahl löschen</button>`)}</div>
    <div class="card table-wrap"><table><thead><tr>${writeOnly(`<th style="width:36px"><input type="checkbox" id="sel-all-p" style="width:16px;height:16px;accent-color:var(--primary);cursor:pointer" /></th>`)}<th>Name</th><th>PLZ</th><th>Ort</th><th>Kategorie</th><th style="width:70px;text-align:center">Aktiv</th><th style="width:50px;text-align:center">Geo</th></tr></thead><tbody></tbody></table></div>`;
  el.querySelector("#btn-add")?.addEventListener("click", () => openForm());
  el.querySelector("#btn-csv")?.addEventListener("click", () => {
    openCsvImportModal({
      title: "CSV Import: Praktikumsstellen",
      duplicateHint: "Bereits vorhandene Praktikumsstellen (gleicher Name) werden übersprungen.",
      fieldDescriptions: [
        { field: "Name",                          hint: "Pflichtfeld" },
        { field: "Straße / Hausnummer / PLZ / Ort", hint: "Optional, Adresse der Einrichtung" },
        { field: "Kategorie",                     hint: "\"Praxis\" oder \"Krankenhaus\"" },
        { field: "Vertragsdatum",                 hint: "Optional, Format: TT.MM.JJJJ" },
        { field: "Fahrzeit (Minuten)",            hint: "Optional, Ganzzahl in Minuten" },
        { field: "Internes Kommentar",            hint: "Optional, nur für die Schule sichtbar" },
        { field: "Aktiv",                         hint: "Optional, \"Nein\" für inaktiv. Leer bedeutet aktiv" },
        { field: "<Fachbereich-Spalten>",         hint: "Optional, Kapazität als Ganzzahl (Anzahl verfügbarer Plätze)" },
        ...(kapazitaetProZeitraumEnabled ? [
          { field: "<Fachbereich> max. pro Zeitraum", hint: "Optional, Ganzzahl. Leer bedeutet unbegrenzt" },
        ] : []),
      ],
      onDownloadTemplate: () => api.practices.csvTemplate(),
      onImport: (file) => api.practices.csvImport(file),
      onComplete: async () => { await load(); if (currentEl) renderTable(currentEl); },
    });
  });
  await load();
  const searchInput = el.querySelector("#search-query") as HTMLInputElement;
  searchInput.addEventListener("input", () => { searchQuery = searchInput.value; renderTable(el); });
  renderTable(el);

  const tableWrap = el.querySelector(".table-wrap") as HTMLElement;
  tableWrap.addEventListener("mouseover", (e) => {
    const td = (e.target as HTMLElement).closest("td[data-full]") as HTMLElement | null;
    if (td) showEllipsisTooltip(td); else hideEllipsisTooltip();
  });
  tableWrap.addEventListener("mouseout", (e) => {
    const td = (e.target as HTMLElement).closest("td[data-full]") as HTMLElement | null;
    if (!td) return;
    const related = (e as MouseEvent).relatedTarget as HTMLElement | null;
    if (related && td.contains(related)) return;
    hideEllipsisTooltip();
  });
  tableWrap.addEventListener("scroll", hideEllipsisTooltip, { passive: true });
  window.addEventListener("scroll", hideEllipsisTooltip, { passive: true });

  // Auswahl-Checkbox + Bulk-Delete Wiring (Header ist statisch, daher nur einmal in render())
  const updateSelP = () => {
    const boxes = [...el.querySelectorAll<HTMLInputElement>(".row-sel")];
    const checked = boxes.filter(b => b.checked);
    const selAll = el.querySelector<HTMLInputElement>("#sel-all-p");
    const delBtn = el.querySelector<HTMLButtonElement>("#btn-del-sel-p");
    if (selAll) { selAll.checked = checked.length === boxes.length && boxes.length > 0; selAll.indeterminate = checked.length > 0 && checked.length < boxes.length; }
    if (delBtn) delBtn.disabled = checked.length === 0;
  };
  (window as any).__pUpdateSel = updateSelP;
  el.querySelector<HTMLInputElement>("#sel-all-p")?.addEventListener("change", (e) => {
    const cb = e.target as HTMLInputElement;
    el.querySelectorAll<HTMLInputElement>(".row-sel").forEach(b => b.checked = cb.checked);
    updateSelP();
  });
  el.querySelector<HTMLButtonElement>("#btn-del-sel-p")?.addEventListener("click", async () => {
    const ids = [...el.querySelectorAll<HTMLInputElement>(".row-sel:checked")].map(b => +b.dataset.id!);
    if (!ids.length) return;
    if (!confirm(`${ids.length} Praktikumsstellen löschen?`)) return;
    try { await Promise.all(ids.map(id => api.practices.delete(id))); await load(); renderTable(el); }
    catch (err: any) { alert("Fehler: " + err.message); }
  });
}

async function geocodeAddress(street: string, houseNo: string, plz: string, ort: string): Promise<{ lat: number; lng: number } | null> {
  const parts = [street, houseNo, plz, ort].filter(Boolean);
  if (parts.length < 2) return null;
  try {
    const r = await api.geocode.lookup(street, houseNo, plz, ort);
    if (r.lat != null && r.lng != null) return { lat: r.lat, lng: r.lng };
  } catch {
    // Die Adresse gehört nicht ins Log.
    console.warn("[Meduvio] Geocoding fehlgeschlagen.");
  }
  return null;
}

async function renderPlanungsTab(practiceId: number, practice: Practice, container: HTMLElement) {
  const tabContent = container.querySelector<HTMLElement>('[data-tab-content="planung"]')!;
  tabContent.innerHTML = `<p style="color:var(--gray-400);font-size:.875rem">Lade Zeiträume…</p>`;
  try {
    const periods = await api.periods.list();
    if (periods.length === 0) {
      tabContent.innerHTML = `<p style="color:var(--gray-400);font-size:.875rem">Keine Planungszeiträume vorhanden.</p>`;
      return;
    }

    const activePeriod = periods.find((p: PlanningPeriod) => p.active) || periods[0];
    let selectedPeriodId = activePeriod.id;
    let allAssignments: any[] = [];
    let filterDept = "";
    let filterClass = "";
    let filterName = "";

    const periodOptions = periods.map((p: PlanningPeriod) =>
      `<option value="${p.id}" ${p.id === selectedPeriodId ? "selected" : ""}>${esc(p.name)}</option>`
    ).join("");

    tabContent.innerHTML = `
      <div style="display:flex;gap:.75rem;align-items:flex-end;margin-bottom:.5rem;width:100%">
        <div class="form-group" style="margin:0;flex:0 0 auto">
          <label style="font-size:.8125rem;margin-bottom:.25rem;display:block">Zeitraum</label>
          <select id="plan-period">${periodOptions}</select>
        </div>
        <div class="form-group" style="margin:0;flex:1 1 0">
          <label style="font-size:.8125rem;margin-bottom:.25rem;display:block">Fachbereich</label>
          <select id="plan-dept" style="width:100%"><option value="">Alle</option></select>
        </div>
        <div class="form-group" style="margin:0;flex:1 1 0">
          <label style="font-size:.8125rem;margin-bottom:.25rem;display:block">Klasse</label>
          <select id="plan-class" style="width:100%"><option value="">Alle</option></select>
        </div>
        <div style="align-self:flex-end;position:relative;flex:0 0 auto">
          <button id="plan-export" class="btn btn-secondary btn-sm" style="white-space:nowrap">Export ▾</button>
          <div id="plan-export-menu" style="display:none;position:absolute;top:calc(100% + 4px);right:0;background:#fff;border:1px solid var(--gray-200);border-radius:.375rem;box-shadow:0 4px 12px rgba(0,0,0,.12);z-index:200;min-width:140px;overflow:hidden">
            <button id="plan-export-excel" style="display:block;width:100%;text-align:left;padding:.5rem .75rem;border:none;background:none;cursor:pointer;font-size:.875rem;line-height:1.4">Excel (.xlsx)</button>
            <button id="plan-export-pdf" style="display:block;width:100%;text-align:left;padding:.5rem .75rem;border:none;background:none;cursor:pointer;font-size:.875rem;line-height:1.4">PDF</button>
          </div>
        </div>
      </div>
      <div class="filter-bar" style="border:none;background:none;padding:0;margin-top:.5rem">
        <input id="plan-name" type="search" placeholder="Schüler suchen…" />
      </div>
      <div id="plan-table-wrap"></div>`;

    const periodSel = tabContent.querySelector<HTMLSelectElement>("#plan-period")!;
    const deptSel = tabContent.querySelector<HTMLSelectElement>("#plan-dept")!;
    const classSel = tabContent.querySelector<HTMLSelectElement>("#plan-class")!;
    const nameInput = tabContent.querySelector<HTMLInputElement>("#plan-name")!;
    const tableWrap = tabContent.querySelector<HTMLElement>("#plan-table-wrap")!;

    function updateDropdowns() {
      const depts = [...new Map(
        allAssignments.filter(a => a.departmentName)
          .map(a => [a.department, { name: a.departmentName, color: a.departmentColor }])
      ).entries()];
      deptSel.innerHTML = `<option value="">Alle</option>` +
        depts.map(([id, info]) => `<option value="${id}" ${filterDept === id ? "selected" : ""}>${esc(info.name)}</option>`).join("");

      const classes = [...new Set(allAssignments.map(a => a.className).filter(Boolean))].sort() as string[];
      classSel.innerHTML = `<option value="">Alle</option>` +
        classes.map(c => `<option value="${c}" ${filterClass === c ? "selected" : ""}>${esc(c)}</option>`).join("");
    }

    function formatDate(periodStartDate: string, dayOffset: number): string {
      const d = new Date(periodStartDate);
      d.setDate(d.getDate() + dayOffset);
      return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
    }

    function getFiltered() {
      const q = filterName.trim().toLowerCase();
      return allAssignments.filter(a => {
        if (filterDept && a.department !== filterDept) return false;
        if (filterClass && a.className !== filterClass) return false;
        if (q && !(a.studentName || "").toLowerCase().includes(q)) return false;
        return true;
      });
    }

    function renderAssignmentsTable() {
      const filtered = getFiltered();

      if (filtered.length === 0) {
        tableWrap.innerHTML = `<p style="color:var(--gray-400);font-size:.875rem">${
          allAssignments.length === 0 ? "Keine Zuweisungen in diesem Zeitraum." : "Keine Treffer."
        }</p>`;
        return;
      }

      const nowrap = "white-space:nowrap;overflow:hidden;text-overflow:ellipsis";
      const rows = filtered.map(a => {
        const startDate = formatDate(a.periodStartDate, a.startWeek * 7);
        const endDate = formatDate(a.periodStartDate, (a.startWeek + a.durationWeeks) * 7 - 1);
        const dateText = startDate === endDate ? startDate : `${startDate} – ${endDate}`;
        const dot = a.departmentColor
          ? `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${esc(a.departmentColor)};margin-right:.375rem;flex-shrink:0"></span>`
          : "";
        return `<tr>
          <td style="font-weight:500;${nowrap};max-width:140px" title="${esc(a.studentName || "")}">${esc(a.studentName || "–")}</td>
          <td class="text-muted" style="${nowrap};max-width:80px">${esc(a.className || "–")}</td>
          <td style="${nowrap};max-width:100px" title="${esc(a.departmentName || "")}"><span style="display:inline-flex;align-items:center">${dot}<span style="overflow:hidden;text-overflow:ellipsis">${esc(a.departmentShortName || a.departmentName || a.department || "–")}</span></span></td>
          <td class="text-muted" style="white-space:nowrap">${esc(dateText)}</td>
        </tr>`;
      }).join("");

      const deptMap = new Map<string, { name: string; color: string }>(
        allAssignments.filter(a => a.departmentName)
          .map(a => [a.department, { name: a.departmentName, color: a.departmentColor }])
      );
      const summaryHtml = [...deptMap.entries()].map(([, info]) => {
        const dot = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${esc(info.color)};margin-right:.25rem;vertical-align:middle"></span>`;
        return `<span style="font-size:.8125rem;margin-right:.75rem;color:var(--gray-600)">${dot}${esc(info.name)}</span>`;
      }).join("");

      tableWrap.innerHTML = `
        <div class="card table-wrap" style="margin:0;overflow:hidden">
          <table style="table-layout:fixed;width:100%">
            <thead><tr>
              <th style="width:140px">Schüler</th>
              <th style="width:80px">Klasse</th>
              <th style="width:100px">Fachbereich</th>
              <th style="width:130px">Zeitraum</th>
            </tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
        ${summaryHtml ? `<div style="margin-top:.75rem;padding:.5rem 0">${summaryHtml}</div>` : ""}`;
    }

    function buildExportData(): PracticeExportData {
      const periodName = periods.find((p: PlanningPeriod) => p.id === selectedPeriodId)?.name || "";
      return {
        practice,
        periodName,
        assignments: getFiltered().map(a => ({
          studentName: a.studentName || "–",
          className: a.className || "–",
          departmentShortName: a.departmentShortName || a.departmentName || a.department || "–",
          periodStartDate: a.periodStartDate,
          startWeek: a.startWeek,
          durationWeeks: a.durationWeeks,
        })),
      };
    }

    // Export-Dropdown
    const exportBtn = tabContent.querySelector<HTMLButtonElement>("#plan-export")!;
    const exportMenu = tabContent.querySelector<HTMLElement>("#plan-export-menu")!;
    exportBtn.addEventListener("click", e => {
      e.stopPropagation();
      exportMenu.style.display = exportMenu.style.display === "none" ? "block" : "none";
    });
    tabContent.querySelector("#plan-export-excel")!.addEventListener("click", () => {
      exportMenu.style.display = "none";
      exportPracticeAssignments("xlsx", buildExportData());
    });
    tabContent.querySelector("#plan-export-pdf")!.addEventListener("click", () => {
      exportMenu.style.display = "none";
      exportPracticeAssignments("pdf", buildExportData());
    });
    document.addEventListener("click", (e) => {
      if (!exportBtn.parentElement?.contains(e.target as Node)) exportMenu.style.display = "none";
    });

    async function fetchAndRender() {
      tableWrap.innerHTML = `<p style="color:var(--gray-400);font-size:.875rem">Lade Daten…</p>`;
      try {
        allAssignments = await api.practices.assignments(practiceId, selectedPeriodId);
        updateDropdowns();
        renderAssignmentsTable();
      } catch (err: any) {
        tableWrap.innerHTML = `<p style="color:var(--red-700);font-size:.875rem">Fehler: ${esc(err.message)}</p>`;
      }
    }

    periodSel.addEventListener("change", () => { selectedPeriodId = +periodSel.value; fetchAndRender(); });
    deptSel.addEventListener("change", () => { filterDept = deptSel.value; renderAssignmentsTable(); });
    classSel.addEventListener("change", () => { filterClass = classSel.value; renderAssignmentsTable(); });
    nameInput.addEventListener("input", () => { filterName = nameInput.value; renderAssignmentsTable(); });

    await fetchAndRender();
  } catch (err: any) {
    tabContent.innerHTML = `<p style="color:var(--red-700);font-size:.875rem">Fehler: ${esc(err.message)}</p>`;
  }
}
